import { Badge, Card, Group, Stack, Text } from '@mantine/core';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { AnalysisStaff, Step } from '@/api/analyses';
import type { Run } from '@/api/runs';
import { useFormat } from '@/components/useFormat';
import { SchedulingNote } from '@/features/runs/FailureBadge';
import { RunErrorLine } from '@/features/runs/RunErrorLine';
import { runFailure } from '@/features/runs/runError';
import { InternalStateChip } from '@/features/staff/InternalStateChip';
import { brand } from '@/theme/theme';

/** The type's steps as a timeline, each with its runs; superseded runs dimmed. */
export function RunChain({ analysis, steps }: { analysis: AnalysisStaff; steps: Step[] }) {
  const { t } = useTranslation('analyses');
  const { relative } = useFormat();
  const runs = (analysis.runs ?? []) as Run[];
  return (
    <Stack gap="sm">
      {steps.map((s, i) => {
        const mine = runs.filter((r) => r.step_index === i);
        const current = analysis.current_step === i;
        return (
          <Card key={i} p="sm" style={current ? { borderColor: brand.dark } : undefined}>
            <Group justify="space-between" mb={mine.length ? 6 : 0}>
              <Group gap="sm">
                <Text fw={600} fz={14}>
                  {t('chain.step', { n: i + 1 })} · {s.run_type}
                </Text>
                {current && (
                  <Badge size="xs" color="forest" variant="light">
                    {t('chain.current')}
                  </Badge>
                )}
                {analysis.failed_step === i && (
                  <Badge size="xs" color="red" variant="light">
                    {t('chain.failedHere')}
                  </Badge>
                )}
              </Group>
              <Text fz={12} c={brand.textMuted}>
                {mine.length ? t('chain.runs', { count: mine.length }) : t('chain.notRun')}
              </Text>
            </Group>
            {mine.map((r) => {
              const failure = runFailure(r.error);
              return (
                <Stack key={r.public_id} gap={0} py={4} style={r.superseded_by ? { opacity: 0.55 } : undefined}>
                  <Group gap="sm">
                    <Link to="/staff/runs/$runId" params={{ runId: r.public_id }} style={{ fontFamily: 'monospace', fontSize: 12, color: brand.text }}>
                      {r.public_id}
                    </Link>
                    <InternalStateChip state={r.state ?? ''} />
                    <SchedulingNote priority={r.priority} allowOverflow={r.allow_overflow} />
                    {r.superseded_by && (
                      <Badge size="xs" color="gray" variant="light">
                        {t('chain.superseded')}
                      </Badge>
                    )}
                    {typeof r.progress === 'number' && r.progress > 0 && r.progress < 1 && (
                      <Text fz={12} c={brand.textMuted}>
                        {Math.round(r.progress * 100)}{' '}%
                      </Text>
                    )}
                    <Text fz={12} c={brand.textMuted}>
                      {relative(r.created_at)}
                    </Text>
                  </Group>
                  <RunErrorLine run={r} />
                  {failure?.hint && (
                    <Text fz={12} c={brand.textMuted} lineClamp={2} title={failure.hint}>
                      {failure.hint}
                    </Text>
                  )}
                </Stack>
              );
            })}
          </Card>
        );
      })}
    </Stack>
  );
}
